import {
    Belonging,
    BookingBelonging,
    BookingBelongingsPage,
    FetchBookingBelongingsParams
} from '@spike/belonging-model';
import { BelongingsStatus, fetchBelongingsThunk, fetchBookingBelongingsThunk } from '@spike/belongings-action';
import { Option, Period } from '@spike/model';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { TasksDatePeriod } from '../TasksLayout';
import styles from './Belongings.module.scss';
import BelongingsHeader from './BelongingsHeader';
import BelongingsTable, { BelongingsSortField } from './BelongingsTable';
import { bookingBelongingToItem } from './bookingBelongingToItem';
import BelongingsPrintPreview from './Print/BelongingsPrintPreview/BelongingsPrintPreview';
import { nextColumnSort, TableColumnSort } from 'utils/tableSort';

interface BelongingsProps {
    datePeriod: TasksDatePeriod;
    onChangeDate: (period: TasksDatePeriod) => void;
}

export const Belongings: FunctionComponent<BelongingsProps> = ({ datePeriod, onChangeDate }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const [search, setSearch] = useState('');
    const [selectedMarketplaceIds, setSelectedMarketplaceIds] = useState<Array<number>>([]);
    const [selectedRoomIds, setSelectedRoomIds] = useState<Array<number>>([]);
    const [selectedBelongingIds, setSelectedBelongingIds] = useState<Array<number>>([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [sort, setSort] = useState<TableColumnSort<BelongingsSortField> | undefined>(undefined);

    const page = useSelector<RootState, BookingBelongingsPage | undefined>(
        state => state.belongings.bookingBelongingsPage
    );
    const catalog = useSelector<RootState, Array<Belonging>>(state => state.belongings.belongings);
    const isLoading = useSelector<RootState, boolean>(
        state => state.belongings.status === BelongingsStatus.BookingBelongingsFetching
    );

    const [fetchStarted, setFetchStarted] = useState(false);
    useEffect(() => {
        if (isLoading) {
            setFetchStarted(true);
        }
    }, [isLoading]);
    const showSpinner = isLoading || !fetchStarted;

    useEffect(() => {
        dispatch(fetchBelongingsThunk(apiClientWrapper));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const params: FetchBookingBelongingsParams = {
            search: search || undefined,
            marketplaceIds: selectedMarketplaceIds.length ? selectedMarketplaceIds : undefined,
            roomIds: selectedRoomIds.length ? selectedRoomIds : undefined,
            belongingIds: selectedBelongingIds.length ? selectedBelongingIds : undefined,
            startDate: datePeriod.from.format('YYYY-MM-DD'),
            endDate: datePeriod.to.format('YYYY-MM-DD'),
            sortBy: sort?.field,
            sortOrder: sort?.direction
        };
        const timer = setTimeout(() => {
            dispatch(fetchBookingBelongingsThunk(apiClientWrapper, params));
        }, 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, datePeriod, selectedMarketplaceIds, selectedRoomIds, selectedBelongingIds, sort]);

    const items = useMemo(() => (page?.data ?? ([] as Array<BookingBelonging>)).map(bookingBelongingToItem), [page]);

    const belongingOptions = useMemo<Array<Option<number>>>(
        () =>
            catalog
                .filter(belonging => belonging.active)
                .map(belonging => ({ id: belonging.id, name: belonging.name }))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [catalog]
    );

    const handleSearch = (value: string) => setSearch(value);
    const handleChangeDate = (period: TasksDatePeriod) => onChangeDate(period);
    const handleChangeMarketplaces = (ids: Array<number>) => setSelectedMarketplaceIds(ids);
    const handleChangeRooms = (ids: Array<number>) => setSelectedRoomIds(ids);
    const handleChangeBelongings = (ids: Array<number>) => setSelectedBelongingIds(ids);

    const handleSort = (field: BelongingsSortField) => {
        setSort(prev => nextColumnSort(field, prev));
    };

    const dateLabel = datePeriod.from.isSame(datePeriod.to, 'day')
        ? datePeriod.from.format('L')
        : `${datePeriod.from.format('L')} – ${datePeriod.to.format('L')}`;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <BelongingsHeader
                    search={search}
                    datePeriod={datePeriod}
                    selectedMarketplaceIds={selectedMarketplaceIds}
                    selectedRoomIds={selectedRoomIds}
                    belongingOptions={belongingOptions}
                    selectedBelongingIds={selectedBelongingIds}
                    onSearch={handleSearch}
                    onChangeDate={handleChangeDate}
                    onChangeMarketplaces={handleChangeMarketplaces}
                    onChangeRooms={handleChangeRooms}
                    onChangeBelongings={handleChangeBelongings}
                    onPrint={() => setPreviewOpen(true)}
                />
            </div>
            <div className={styles.body}>
                <BelongingsTable
                    items={items}
                    isLoading={showSpinner}
                    sort={sort}
                    onSort={handleSort}
                />
            </div>

            {previewOpen && (
                <BelongingsPrintPreview
                    items={items}
                    dateLabel={dateLabel}
                    onClose={() => setPreviewOpen(false)}
                />
            )}
        </div>
    );
};

export default Belongings;
